import isEqual from 'lodash-es/isEqual';
import {
    logIsBusyResourceSendClicked,
    onIsDraftChanged,
    saveEvent,
    verifyEventThenSave,
} from 'owa-calendar-compose-form-actions';
import isNewEvent from 'owa-calendar-compose-form-store/lib/selectors/isNewEvent';
import {
    getComposeEvent,
    getComposeViewState,
    getOriginalEvent,
    getAttachmentFiles,
} from 'owa-calendar-compose-store-manager';
import getResourceConflictCount from 'owa-calendar-compose-form-store/lib/utils/getResourceConflictCount';
import {
    hasInvalidRecipients,
    isEventAMeeting,
    isOriginalTime,
} from 'owa-calendar-event-capabilities';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { confirm, DialogResponse } from 'owa-confirm-dialog';
import loc, { format } from 'owa-localize';
import { saveButtonLabel } from 'owa-locstrings/lib/strings/savebuttonlabel.locstring.json';
import { sendButtonLabel } from 'owa-locstrings/lib/strings/sendbuttonlabel.locstring.json';
import { orchestrator } from 'satcheljs';
import { verifyAttachmentStatusBeforeSave } from '../utils/verifyAttachmentStatusBeforeSave';
import { validateCollabSpace, collabSpaceConfirmation } from '../utils/collabSpaceValidations';
import { isFeatureEnabled } from 'owa-feature-flags';
import {
    changeRecurrenceConfirmationTitleText,
    changeRecurrenceSubText,
    dontSaveButtonLabel,
    dontSendButtonLabel,
    editInvitationButtonLabel,
    invalidRecipientsDialogDescription,
    invalidRecipientsDialogTitle,
    locationsUnavailable,
    locationUnavailable,
    missingTitleAppointmentDescription,
    missingTitleMeetingDescription,
    missingTitleTitle,
    multipleLocationsConflict,
    oneLocationConflict,
    save,
    sendAnyway,
    singleLocationConflict,
} from './verifyEventThenSave.locstring.json';
import { logUsage } from 'owa-analytics';

export const verifyEventThenSaveOrchestrator = orchestrator(
    verifyEventThenSave,
    async actionMessage => {
        await verifyEventThenSaveInternal(actionMessage.formId, actionMessage.targetWindow);
    }
);

async function verifyEventThenSaveInternal(formId: string, targetWindow: Window) {
    const event = getComposeEvent(formId);
    const originalEvent = getOriginalEvent(formId);
    const attachmentFiles = getAttachmentFiles(formId);

    // Make sure event does not have invalid attendees
    if (
        hasInvalidRecipients(event.RequiredAttendees) ||
        hasInvalidRecipients(event.OptionalAttendees)
    ) {
        // show confirmation dialog
        await confirm(
            loc(invalidRecipientsDialogTitle),
            loc(invalidRecipientsDialogDescription),
            false,
            {
                delayCallbackAfterAnimation: true,
                hideCancelButton: true,
                targetWindow: targetWindow,
            }
        );

        // Don't save anymore, just return
        return;
    }

    // If the event is a recurrence, and the recurrence is modified, we ask for confirmation
    // Since recurrence object doesn't contain start time data we also need to compare the event start times.
    // We also need to validate if originalEvent has ItemId to make sure the dialog does not show for duplicate event scenario
    if (
        event.CalendarItemType != 'ThisAndFollowingInstancesMaster' &&
        originalEvent.Recurrence &&
        originalEvent.ItemId &&
        !originalEvent.IsDraft &&
        (!isEqual(event.Recurrence, originalEvent.Recurrence) ||
            !isEqual(event.Start, originalEvent.Start))
    ) {
        const response: DialogResponse = await confirm(
            loc(changeRecurrenceConfirmationTitleText),
            loc(changeRecurrenceSubText),
            false,
            {
                okText: loc(save),
                targetWindow: targetWindow,
            }
        );
        if (response !== DialogResponse.ok) {
            // If we don't choose save, then do nothing
            return;
        }
    }

    const isNew: boolean = isNewEvent(formId);

    let isCollabSpaceValid = false;
    if (isFeatureEnabled('cal-cmp-fluidCollaborativeSpace')) {
        isCollabSpaceValid = await validateCollabSpace(
            getComposeViewState(formId).collabSpaceViewStateId
        );
        if (!isCollabSpaceValid) {
            logUsage(
                'ShowCollabSpaceBlockingDialog',
                {
                    isNewEvent: isNew,
                },
                { isCore: true }
            );
            const response: DialogResponse = await collabSpaceConfirmation(
                getComposeViewState(formId).collabSpaceViewStateId
            );
            if (response !== DialogResponse.ok) {
                return;
            }
        }
    }

    let allowSaveWithSetLocation: boolean = true;

    // Make sure event doesn't have location conflicts, after changes to the event scheduling.
    // Do not check if this is editing an event with no time changes, or if it is editing a recurrence
    // We don't have accurate Free Busy for editing recurrence that would get the avaialability of the room for the whole series
    // and yet concider the room to be free at the original event times. So we don't check for conflict for editing recurrences
    // until server supports it.
    if (
        event.Resources?.length > 0 &&
        (isNew || (!isOriginalTime(originalEvent, event) && !event.Recurrence))
    ) {
        // Get conflict count, but not counting conflicts with the original event for existing meeting;
        // no filter applied for new event
        const locationConflictCount: number = await getResourceConflictCount(
            event.Resources,
            event.Start,
            event.End,
            event.IsAllDayEvent,
            isNew ? event.Recurrence : null,
            event.RequiredAttendees,
            event.OptionalAttendees,
            isNew ? undefined : originalEvent
        );

        if (locationConflictCount > 0) {
            // show confirmation dialog, allow to save anyway with introduced conflicts, or back to Edit
            const busyLocationTitle: string =
                locationConflictCount > 1
                    ? format(loc(locationsUnavailable), locationConflictCount)
                    : loc(locationUnavailable);
            const busyLocationsMsg: string =
                locationConflictCount > 1
                    ? format(loc(multipleLocationsConflict), locationConflictCount)
                    : event.Resources.length > 1
                    ? loc(oneLocationConflict)
                    : loc(singleLocationConflict);

            const noSendButtonLabel: string = isNew
                ? loc(dontSendButtonLabel)
                : loc(editInvitationButtonLabel);

            if (
                (await confirm(busyLocationTitle, busyLocationsMsg, false, {
                    delayCallbackAfterAnimation: true,
                    okText: loc(sendAnyway),
                    cancelText: noSendButtonLabel,
                    targetWindow: targetWindow,
                })) === DialogResponse.ok
            ) {
                // Allow saving the event with busy locations
                allowSaveWithSetLocation = true;
                logIsBusyResourceSendClicked(true, formId);
            } else {
                // Don't allow saving, return for the user to select a new location
                allowSaveWithSetLocation = false;
                logIsBusyResourceSendClicked(false, formId);
                return;
            }
        }
    }

    if (
        !(await verifyAttachmentStatusBeforeSave(
            event,
            !!attachmentFiles,
            isCollabSpaceValid,
            formId,
            targetWindow
        ))
    ) {
        return;
    }

    const { showConfirmationDialog, title, subText, okText, cancelText } = getDialogStrings(
        event,
        isNew
    );

    if (
        allowSaveWithSetLocation &&
        (!showConfirmationDialog ||
            (await confirm(title, subText, false, {
                // Wait until dialog is dismissed to avoid focus interruption
                delayCallbackAfterAnimation: true,
                okText: okText,
                cancelText: cancelText,
                targetWindow: targetWindow,
            })) === DialogResponse.ok)
    ) {
        onIsDraftChanged(false, formId);
        saveEvent(formId);
    }
}

function getDialogStrings(
    event: CalendarEvent,
    isNewEvent: boolean
): {
    showConfirmationDialog: boolean;
    title?: string;
    subText?: string;
    okText?: string;
    cancelText?: string;
} {
    if (isNewEvent && (!event.Subject || event.Subject.length == 0)) {
        if (isEventAMeeting(event)) {
            return {
                showConfirmationDialog: true,
                title: loc(missingTitleTitle),
                subText: loc(missingTitleMeetingDescription),
                okText: loc(sendButtonLabel),
                cancelText: loc(dontSendButtonLabel),
            };
        } else {
            return {
                showConfirmationDialog: true,
                title: loc(missingTitleTitle),
                subText: loc(missingTitleAppointmentDescription),
                okText: loc(saveButtonLabel),
                cancelText: loc(dontSaveButtonLabel),
            };
        }
    }

    return {
        showConfirmationDialog: false,
    };
}
